import { useState, type ReactNode } from 'react';
import { Alert, Button, Card, Modal, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { deregisterDevice, listDevices, type PADevice, type PADevices } from '../api/access';
import { useCapabilities } from '../access/useCapabilities';
import { getDashboard, type PAAccount, type PADashboard } from '../api/money';
import { formatInstant } from '../format';
import { FactList } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import { deviceIdentityLabel, deviceOriginLabel, leaseStateLabel } from '../money/labels';
import { errorText } from '../session/errorText';

/**
 * ============================================================================
 * «МОИ УСТРОЙСТВА» — the registered Devices and their de-registration (§24, F23)
 * ============================================================================
 *
 * # ⚠ WHAT DE-REGISTRATION DOES, EXACTLY, AND WHY THE DIALOG READS THE SERVER
 *
 * It archives the BINDING and frees a slot within the Account's device limit. It does NOT
 * cut a live session: the domain soft-deletes and returns, with no outbox event, no lease
 * release and no CoA, and there is **no `device.deregistered` event anywhere in this
 * system**. §24's own prose is the truth — «the affected device falls back to the captive
 * state at its NEXT authorization» — and `GET /pa/accounts/capabilities` publishes
 * `device_deregister_cuts_session: false` precisely so a confirmation dialog cannot promise
 * otherwise. The dialog therefore READS that flag rather than hard-coding either sentence:
 * when a later wave builds the enforcement the flag becomes true and the wording follows it,
 * and until then a subscriber is not told his connection will drop when it will not.
 *
 * ⚠ AND WHEN THE CAPABILITY READ HAS NOT LANDED THE DIALOG SAYS NEITHER. It is the one
 * fail-closed direction available for a claim (as opposed to a control): asserting «the
 * connection drops» would be a promise the server may not keep, and asserting «it does not»
 * would be a promise about a build we have not asked. What it certainly does — the slot and
 * the fallback to the enrollment page — is said unconditionally.
 *
 * # The address column is joined ON THE SERVER
 *
 * §2.20 asks for «Current IP and lease state», and the ADMIN card gets it by a second,
 * separately-permissioned browser call to `/ip-pools/leases?account_id=` — a router that is
 * deliberately NOT on this listener, because it also carries pool CRUD, static pinning and
 * revocation. So the wire carries `address`, `lease_state` and `address_known` already
 * correlated, and this screen must not reproduce the admin trick.
 *
 * ⚠ `address_known: false` RENDERS «неизвестно» AND NEVER «нет адреса». The first means the
 * lease read did not happen — no IP-pool service, or a failed read — so an empty `address`
 * says nothing at all about the device; the second is a fact about the device. To a
 * subscriber diagnosing his own connection they are opposite answers, and rendering the
 * second for the first is a screen that lies calmly.
 *
 * # What is deliberately NOT here
 *
 *   - **REGISTRATION, and any MAC field.** A device is registered on the ENROLLMENT portal,
 *     where its identity context is known; a portal registration would have to take a MAC
 *     from the request, which is the device-hijack primitive this system closed (D23). The
 *     lead says how to register instead, because a screen that only refuses is a telephone
 *     call.
 *   - **`trap_switch_id` / `registered_equipment_id`.** They are not on the wire: an
 *     internal primary key of the OPERATOR's equipment is not part of «his device's
 *     identity», and there is nothing he can do with it.
 *   - **the per-limit «choose which to replace» flow.** That belongs to the enrollment
 *     portal, which runs it at the moment a registration needs a slot (Amendment 02 §4.2).
 *     This screen gives the same ability outside the captive flow, which is all §24 asks.
 */
export function DevicesScreen() {
  return (
    <MoneyPage titleKey="devices.title" leadKey="devices.lead">
      {(contract) => <DevicesBody contractID={contract.id} />}
    </MoneyPage>
  );
}

function DevicesBody({ contractID }: { contractID: number }) {
  const { t } = useTranslation();
  /*
    ⚠ THE ACCOUNT LIST COMES FROM THE DASHBOARD READ, and that is a choice worth stating.

    Devices are ACCOUNT-scoped — `GET /pa/accounts/{id}/devices` — so this screen needs an
    account before it can ask anything, and §24 reaches the list exactly that way («click
    the Account login»). `GET /pa/money/dashboard` is this SPA's one canonical per-contract
    account list, already carrying the login and the status, and reusing it means the picker
    here and the cards on the landing page cannot come to disagree about which accounts
    exist. The alternative — `GET /pa/accounts/blockings` — would pull every account's whole
    block history to populate a dropdown.
  */
  const accounts = usePAQuery<PADashboard>(() => getDashboard(contractID), [contractID]);
  const caps = useCapabilities();
  const [chosen, setChosen] = useState<number | null>(null);

  return (
    <QueryFrame query={accounts.query} reload={accounts.reload}>
      {(dashboard) => {
        const rows = dashboard.accounts;
        if (rows.length === 0) {
          return <Alert type="info" showIcon message={t('devices.noAccounts')} />;
        }
        // The explicit choice, or the first account. `chosen` is re-resolved against the
        // freshly loaded list, so a reload never leaves the picker on an account the
        // contract no longer serves.
        const account = rows.find((a) => a.account_id === chosen) ?? rows[0];
        // ⚠ THREE STATES AND NOT TWO: the server's `true`, its `false`, and `null` for «the
        // capability read has not landed». `capabilityOr` is deliberately NOT used here,
        // because it collapses the third into a boolean — and the third is the one where the
        // dialog must claim nothing about the live session at all.
        const cutsSession =
          caps.query.status === 'ready' ? caps.query.data.device_deregister_cuts_session : null;
        return (
          <>
            {/* ⚠ NO PICKER FOR A SINGLE ACCOUNT. Most subscribers have exactly one, and a
                dropdown with one option is the dead control this phase has already removed
                three times (the language switch, the recovery-channel radio, the
                one-contract chooser). */}
            {rows.length > 1 ? (
              <AccountPicker rows={rows} value={account.account_id} onChange={setChosen} />
            ) : null}
            <DeviceList contractID={contractID} account={account} cutsSession={cutsSession} />
          </>
        );
      }}
    </QueryFrame>
  );
}

function AccountPicker({
  rows,
  value,
  onChange,
}: {
  rows: PAAccount[];
  value: number;
  onChange: (id: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="pa-picker">
      <label className="pa-picker-label" htmlFor="pa-device-account">
        {t('devices.accountLabel')}
      </label>
      <Select<number>
        id="pa-device-account"
        className="pa-picker-select"
        value={value}
        onChange={onChange}
        options={rows.map((a) => ({ value: a.account_id, label: a.login }))}
      />
    </div>
  );
}

/**
 * The devices of ONE account.
 *
 * `cutsSession` is the SERVER's answer, or `null` when the capability read has not landed —
 * see this file's header for why the third state is not collapsed into `false`.
 */
function DeviceList({
  contractID,
  account,
  cutsSession,
}: {
  contractID: number;
  account: PAAccount;
  cutsSession: boolean | null;
}) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PADevices>(
    () => listDevices(contractID, account.account_id),
    [contractID, account.account_id],
  );
  const [confirming, setConfirming] = useState<PADevice | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');

  const deregister = async () => {
    if (!confirming) return;
    setBusy(true);
    setError(null);
    setNotice('');
    try {
      await deregisterDevice(contractID, account.account_id, confirming.id);
      setNotice(t('devices.deregistered'));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
      setConfirming(null);
      // The SLOT COUNT changes, and `used` is the server's figure — so the list is re-read
      // rather than patched. `items.length` is not the count §2.20 asks for.
      reload();
    }
  };

  return (
    <QueryFrame query={query} reload={reload}>
      {(data) => (
        <>
          {notice ? (
            <Alert type="success" showIcon message={notice} style={{ marginBottom: 16 }} />
          ) : null}
          {error ? (
            <Alert type="error" showIcon message={errorText(error, t)} style={{ marginBottom: 16 }} />
          ) : null}

          <Card className="pa-block">
            <FactList
              facts={[
                {
                  key: 'limit',
                  label: t('devices.accountLabel'),
                  value: <span className="mono">{account.login}</span>,
                  // ⚠ `used` AND `device_limit` ARE THE SERVER'S TWO FIGURES. `items.length`
                  // is not «slots in use»: the two can differ, and §2.20 asks for the count
                  // because the ENROLLMENT portal's behaviour depends on it — at limit 1 a
                  // new registration AUTO-REPLACES the existing device.
                  hint: t('devices.limit', { used: data.used, limit: data.device_limit }),
                },
              ]}
            />
            <p className="pa-field-hint">{t('devices.limitHint')}</p>
          </Card>

          {data.items.length === 0 ? (
            <Alert type="info" showIcon message={t('devices.empty')} />
          ) : null}

          {data.items.map((device) => (
            <Card
              key={device.id}
              className="pa-block"
              title={<span className="mono">{device.mac}</span>}
              data-testid={`pa-device-${device.id}`}
            >
              <FactList facts={deviceFacts(device, t)} />
              <div className="pa-actions">
                <Button danger size="large" block onClick={() => setConfirming(device)}>
                  {t('devices.deregister')}
                </Button>
              </div>
            </Card>
          ))}

          <Modal
            open={confirming !== null}
            title={t('devices.deregisterTitle')}
            okText={t('devices.deregisterConfirm')}
            cancelText={t('devices.deregisterBack')}
            okButtonProps={{ danger: true, loading: busy }}
            onOk={() => void deregister()}
            onCancel={() => setConfirming(null)}
          >
            <ul className="pa-warnings">
              <li>{t('devices.deregisterFrees')}</li>
              <li>{t('devices.deregisterNext')}</li>
              {/* ⚠ THE SERVER DECIDES WHICH SENTENCE, AND «NEITHER» IS A REAL THIRD CASE. */}
              {cutsSession === true ? <li>{t('devices.deregisterCutsSession')}</li> : null}
              {cutsSession === false ? <li>{t('devices.deregisterKeepsSession')}</li> : null}
            </ul>
          </Modal>
        </>
      )}
    </QueryFrame>
  );
}

function deviceFacts(
  device: PADevice,
  t: TFunction,
): Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> {
  const facts: Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> = [
    {
      key: 'identity',
      label: t('devices.identityLabel'),
      value: deviceIdentityLabel(device.identity_form, t),
    },
    {
      key: 'address',
      label: t('devices.addressLabel'),
      value: addressValue(device, t),
      hint: addressHint(device, t),
    },
    {
      key: 'registered',
      label: t('devices.registeredLabel'),
      value: formatInstant(device.registered_at),
    },
    {
      key: 'origin',
      label: t('devices.originLabel'),
      value: deviceOriginLabel(device.origin, t),
    },
    {
      key: 'online',
      label: t('devices.lastOnlineLabel'),
      value: device.last_online_at
        ? formatInstant(device.last_online_at)
        : t('devices.lastOnlineNever'),
    },
  ];
  // The Option-82 qualifiers and the learned port, each only when it was captured: an empty
  // «Порт коммутатора» row on a MAC-only binding is a fact the operator never recorded.
  if (device.opt82_circuit) {
    facts.splice(1, 0, {
      key: 'circuit',
      label: t('devices.opt82CircuitLabel'),
      value: <span className="mono">{device.opt82_circuit}</span>,
    });
  }
  if (device.opt82_remote) {
    facts.splice(facts.length - 3, 0, {
      key: 'remote',
      label: t('devices.opt82RemoteLabel'),
      value: <span className="mono">{device.opt82_remote}</span>,
    });
  }
  if (device.trap_port !== null) {
    facts.splice(facts.length - 3, 0, {
      key: 'port',
      label: t('devices.portLabel'),
      value: <span className="mono">{String(device.trap_port)}</span>,
    });
  }
  return facts;
}

/**
 * addressValue is the §2.20 column, and the whole point of this function is the THREE
 * outcomes — see the file header.
 *
 *   address_known && address  → the address, with its lease state beside it
 *   address_known && !address → «нет адреса»: a fact about the device
 *   !address_known            → «неизвестно»: a fact about US
 */
function addressValue(device: PADevice, t: TFunction): ReactNode {
  if (!device.address_known) return t('devices.addressUnknown');
  if (!device.address) return t('devices.addressNone');
  const lease = leaseStateLabel(device.lease_state, t);
  return (
    <>
      <span className="mono">{device.address}</span>
      {lease ? <span className="pa-status pa-status-badge">{lease}</span> : null}
    </>
  );
}

function addressHint(device: PADevice, t: TFunction): ReactNode {
  if (!device.address_known) return t('devices.addressUnknownHint');
  if (!device.address) return t('devices.addressNoneHint');
  return undefined;
}
